import { useCallback, useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import {
  getValidFirmAccessToken,
  SESSION_EXPIRED_EVENT,
} from './auth/firmSession'

export default function ProtectedRoute() {
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'unauthenticated' | 'error'>('checking')

  const checkSession = useCallback(async () => {
    setStatus('checking')
    try {
      const token = await getValidFirmAccessToken()
      setStatus(token ? 'authenticated' : 'unauthenticated')
    } catch {
      // Keep stored credentials during temporary network/server trouble.
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const check = async () => {
      try {
        const token = await getValidFirmAccessToken()
        if (mounted) setStatus(token ? 'authenticated' : 'unauthenticated')
      } catch {
        if (mounted) setStatus('error')
      }
    }

    const handleSessionExpired = () => {
      if (mounted) setStatus('unauthenticated')
    }

    check()
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
    return () => {
      mounted = false
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
    }
  }, [])

  if (status === 'checking') {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Checking your session…</div>
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-gray-700">We could not verify your session. Check your connection and try again.</p>
        <button
          type="button"
          onClick={checkSession}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Try again
        </button>
      </div>
    )
  }

  if (status === 'unauthenticated') return <Navigate to="/login-firm" replace />
  return <Outlet />
}
